import { materialTransformationFromDerivedFood } from '../labels/materialDerivedFood.js';

type WarehouseFood = {
  id: string;
  name: string;
  category?: string | null;
  storage_type?: string | null;
  creation_source?: string | null;
  current_expiration_date?: string | null;
};
type WarehouseLot = { food_id: string; qty: number; expires: string | null };

/** Preserve Array.find's first exact lot match, including null versus blank lots. */
export function indexWarehouseLotReceipts(stockIn: readonly {
  payload: { food_id: string; batch_lot?: string | null };
  client_ts: string;
}[]): Map<string, Map<string | null, string>> {
  const receipts = new Map<string, Map<string | null, string>>();
  for (const event of stockIn) {
    const { food_id, batch_lot } = event.payload;
    let lots = receipts.get(food_id);
    if (!lots) receipts.set(food_id, lots = new Map());
    const lot = batch_lot ?? null;
    if (!lots.has(lot)) lots.set(lot, event.client_ts);
  }
  return receipts;
}

export type WarehouseFilters = {
  storage: string;
  status: 'all' | 'open' | 'thawed' | 'expiring';
  category: string;
  sort: 'name' | 'category' | 'expiry';
  expirationLeadHours: number;
  now: number;
};

function expiryTime(value: string | null | undefined): number {
  const time = value ? Date.parse(value) : NaN;
  return Number.isFinite(time) ? time : Infinity;
}

/** Both tabs consume this same ordered product list, before their own search. */
export function selectWarehouseFoods<T extends WarehouseFood>(
  foods: readonly T[],
  lots: readonly WarehouseLot[],
  filters: WarehouseFilters,
): T[] {
  const firstExpiry = new Map<string, number>();
  for (const lot of lots) {
    if (lot.qty <= 0 || !Number.isFinite(lot.qty)) continue;
    firstExpiry.set(lot.food_id, Math.min(
      firstExpiry.get(lot.food_id) ?? Infinity, expiryTime(lot.expires),
    ));
  }
  const expiry = (food: T): number => firstExpiry.get(food.id)
    ?? expiryTime(food.current_expiration_date);
  const statusMatches = (food: T): boolean => {
    if (filters.status === 'all') return true;
    if (filters.status === 'expiring') {
      return expiry(food) - filters.now < filters.expirationLeadHours * 3_600_000;
    }
    const transformation = materialTransformationFromDerivedFood(food);
    return transformation === (filters.status === 'thawed' ? 'thaw' : 'open');
  };
  return foods.filter((food) =>
    (filters.storage === 'all' || food.storage_type === filters.storage)
    && statusMatches(food)
    && (filters.category === 'all' || food.category === filters.category),
  ).sort((a, b) => {
    if (filters.sort === 'category') {
      const categoryOrder = (a.category || 'zzz').localeCompare(b.category || 'zzz', 'it');
      if (categoryOrder) return categoryOrder;
    }
    if (filters.sort === 'expiry') {
      const left = expiry(a);
      const right = expiry(b);
      if (left !== right) return left < right ? -1 : 1;
    }
    return a.name.localeCompare(b.name, 'it') || a.id.localeCompare(b.id);
  });
}
